/**
 * Returns the requested theme as-is — all themes are available to all users.
 * The isProActive parameter is kept for API compatibility but no longer gates themes.
 * 
 * @param {string} requestedTheme - The theme ID requested
 * @param {boolean} isProActive - Whether the user has pro access (unused, kept for compat)
 * @returns {string} - The validated theme ID
 * @since 3.9.4
 */
function validateThemeAccess(requestedTheme, isProActive) {
    // All themes are free — return as-is.
    // Normalize: if just a number, prefix with 'theme-'
    if ( requestedTheme && /^\d+$/.test(requestedTheme) ) {
        return 'theme-' + requestedTheme;
    }
    return requestedTheme || 'theme-0';
}

/**
 * 
 * @param {*} formData 
 * @returns 
 */

function generateRenderedForm(formData, ispro, theme = 'theme-0') {
  // Validate theme access for free users
  theme = validateThemeAccess(theme, ispro);
  
  let html = '';
  let currentPage = 1;
  let totalPages = 1;
  let isPageStarted = false;
  let hasSubmitButton = false;
  let progressIndicatorSettings = null;

  // Add theme class to the main container
  html += `
    <style>
      .simple_form_content {
        font-family: 'Arial', sans-serif;
      }
      
      .simple_form_content .page {
        display: none;
      }
      
      .simple_form_content .page.active {
        display: block;
      }
      
      /* Navigation container */
      .navigation-buttons {
        display: flex;
        justify-content: space-between;
        margin-top: 20px;
        padding: 10px 0;
      }
      
      .navigation-buttons.first-page {
        justify-content: flex-end;
      }
      
      .navigation-buttons.with-submit {
        justify-content: space-between;
      }
      
      /* Button styles - basic shared styles */
      .simple_form_content .form-btn {
        border: none;
        border-radius: 5px;
        padding: 7px 20px;
        cursor: pointer;
        font-size: 16px;
        height: 38px;
        margin-right: 10px;
      }
      
      .simple_form_content .next-btn,
      .simple_form_content .prev-btn,
      .simple_form_content .submit-btn {
        background-color: #a177f8;
        color: #fff;
      }
      
      /* Responsive design */
      @media (max-width: 768px) {
        .form-row.horizontal {
          flex-direction: column;
        }
        
        .navigation-buttons {
          flex-direction: row;
          justify-content: center;
        }
        
        .simple_form_content .form-btn {
          margin: 5px;
        }
      }
      
      /* Signature panel styles */
      .signature-panel {
        border: 1px solid gray;
        padding: 5px;
        width: 100%;
        max-width: 300px;
      }
      
      /* Row layout styles */
      .form-row {
        display: flex;
        margin-bottom: 15px;
      }
      
      .form-row.horizontal {
        flex-direction: row;
        gap: 15px;
      }
      
      .form-row.vertical {
        flex-direction: column;
        gap: 10px;
      }
      
      /* Field styles */
      .text-fields {
        margin-bottom: 15px;
        width: 100%;
      }
      
      .text-fields input:not([type="checkbox"]):not([type="radio"]),
      .text-fields select,
      .text-fields textarea {
        width: 100%;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        box-sizing: border-box;
        margin-top: 5px;
      }
      
      .text-fields label {
        display: block;
        margin-bottom: 5px;
        font-weight: 500;
      }

      /* Required field indicator styles */
      .required-indicator {
        color: #e32;
        margin-left: 3px;
        font-weight: bold;
      }
      
      /* Add tooltip for required fields */
      .required-field-label {
        position: relative;
      }
      
      .required-indicator:hover::after {
        content: 'This field is required';
        position: absolute;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        z-index: 99;
        left: 0;
        top: 100%;
        margin-top: 5px;
      }

      /* Progress Indicator Styles */
      .progress-indicator {
        margin-bottom: 20px;
        text-align: center;
      }
      
      .progress-dots {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;
        margin-top: 10px;
      }
      
      .progress-dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color: #ddd;
        transition: background-color 0.3s ease;
      }
      
      .progress-dot.active {
        background-color: #a177f8;
      }
      
      .progress-dot.completed {
        background-color: #4caf50;
      }
      
      .progress-numbers {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 15px;
        margin-top: 10px;
      }
      
      .progress-number {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background-color: #ddd;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        color: #666;
        transition: all 0.3s ease;
      }
      
      .progress-number.active {
        background-color: #a177f8;
        color: white;
      }
      
      .progress-number.completed {
        background-color: #4caf50;
        color: white;
      }
      
      .progress-bar-container {
        width: 100%;
        background-color: #ddd;
        border-radius: 10px;
        height: 8px;
        margin-top: 10px;
        overflow: hidden;
      }
      
      .progress-bar {
        height: 100%;
        background-color: #a177f8;
        border-radius: 10px;
        transition: width 0.3s ease;
      }
      
      .progress-label {
        font-weight: 500;
        margin-bottom: 5px;
        color: #333;
      }

      /* Conditional field styles */
      .conditional-field {
        transition: opacity 0.3s ease, height 0.3s ease;
      }
      
      .conditional-field.hidden {
        display: none !important;
      }


    </style>
  `;

  // Add theme-specific styles
  html += getThemeStyles(theme); // No need now

  // Count total pages and find progress indicator settings
  totalPages = countTotalPages(formData);
  progressIndicatorSettings = findProgressIndicatorSettings(formData);

  // Start the form container with the selected theme
  html += `<div class="simple_form_content_form_area ${theme}">`;

  // console.log(progressIndicatorSettings)

  // Add progress indicator if enabled
  /* console.log('Progress indicator check:', {
    progressIndicatorSettings: progressIndicatorSettings,
    showProgressIndicator: progressIndicatorSettings ? progressIndicatorSettings.showProgressIndicator : null,
    totalPages: totalPages
  }); */

  if (progressIndicatorSettings && (progressIndicatorSettings.showProgressIndicator === true || progressIndicatorSettings.showProgressIndicator === "true") && totalPages > 1) {
    // console.log('Adding progress indicator');
    html += generateProgressIndicator(progressIndicatorSettings, currentPage, totalPages);
  } else {
    // console.log('Not adding progress indicator');
  }

  // Start with the first page
  html += `<div class="page" data-page="${currentPage}">`;
  isPageStarted = true;

  // Process the form data
  processFormData(formData);

  // Close the last page if it's still open
  if (isPageStarted) {
    // Check for submit button in the last page
    const hasSubmitOnLastPage = checkForSubmitButton(formData);

    // Add navigation for the last page
    if (hasSubmitOnLastPage) {
      if (currentPage > 1) {
        html += `<div class="navigation-buttons with-submit">
                  <button type="button" class="form-btn prev-btn" data-page="${currentPage - 1}">Previous</button>
                </div>`;
      }
    } else {
      if (currentPage > 1) {
        html += `<div class="navigation-buttons">
                  <button type="button" class="form-btn prev-btn" data-page="${currentPage - 1}">Previous</button>
                </div>`;
      }
    }

    html += `</div>`;
  }

  // Close the form container
  html += `</div>`;

  return html;

  // Function to count total pages in the form
  function countTotalPages(data) {
    let pages = 1;
    data.forEach((item) => {
      if (item.type === 'row') {
        item.fields.forEach((field) => {
          if (field.type === 'page_break') {
            pages++;
          }
        });
      } else if (item.type === 'page_break') {
        pages++;
      }
    });
    return pages;
  }

  // Function to find progress indicator settings from page breaks
  function findProgressIndicatorSettings(data) {
    // console.log('Searching for progress indicator in data:', data);
    // Check if ANY page break has progress indicator enabled
    for (const item of data) {
      if (item.type === 'row') {
        for (const field of item.fields) {
          if (field.type === 'page_break') {
            // console.log('Found page break field:', field);
            // Check for both boolean true and string "true"
            if (field.showProgressIndicator === true || field.showProgressIndicator === "true") {
              // console.log('Progress indicator enabled on field:', field);
              return field;
            }
          }
        }
      } else if (item.type === 'page_break') {
        // console.log('Found page break item:', item);
        // Check for both boolean true and string "true"
        if (item.showProgressIndicator === true || item.showProgressIndicator === "true") {
          // console.log('Progress indicator enabled on item:', item);
          return item;
        }
      }
    }
    // console.log('No progress indicator found');
    return null;
  }

  // Function to generate progress indicator HTML
  function generateProgressIndicator(settings, currentPage, totalPages) {
    const label = settings.progressLabel || 'Step';
    const style = settings.progressStyle || 'dots';

    let html = `<div class="progress-indicator" id="progress-indicator">`;
    html += `<div class="progress-label">${label} ${currentPage} of ${totalPages}</div>`;

    if (style === 'dots') {
      html += `<div class="progress-dots">`;
      for (let i = 1; i <= totalPages; i++) {
        const className = i < currentPage ? 'completed' : (i === currentPage ? 'active' : '');
        html += `<div class="progress-dot ${className}" data-page="${i}"></div>`;
      }
      html += `</div>`;
    } else if (style === 'numbers') {
      html += `<div class="progress-numbers">`;
      for (let i = 1; i <= totalPages; i++) {
        const className = i < currentPage ? 'completed' : (i === currentPage ? 'active' : '');
        html += `<div class="progress-number ${className}" data-page="${i}">${i}</div>`;
      }
      html += `</div>`;
    } else if (style === 'bar') {
      const percentage = ((currentPage - 1) / (totalPages - 1)) * 100;
      html += `<div class="progress-bar-container">`;
      html += `<div class="progress-bar" style="width: ${percentage}%"></div>`;
      html += `</div>`;
    }

    html += `</div>`;
    return html;
  }

  // Function to check if the form data contains a submit button
  function checkForSubmitButton(data) {
    for (const item of data) {
      if (item.type === 'submit') {
        return true;
      }

      if (item.type === 'row' && item.fields) {
        for (const field of item.fields) {
          if (field.type === 'submit') {
            return true;
          }
        }
      }
    }
    return false;
  }

  // Function to process the entire form data array
  function processFormData(data) {
    let submitButtonFound = false;

    data.forEach((item) => {
      if (item.type === 'row') {
        processRow(item);
      } else if (item.type === 'submit') {
        submitButtonFound = true;
        processField(item);
      } else {
        // If it's a direct field (not in a row)
        processField(item);
      }
    });

    hasSubmitButton = submitButtonFound;
  }

  // Function to process a row and its fields
  function processRow(row) {
    let currentRowHtml = ''; // Collect HTML for this row
    let rowStarted = false;
    let pageBreakInRow = false;
    let submitButtonInRow = false;

    // First pass to check if there's a submit button in this row
    for (let i = 0; i < row.fields.length; i++) {
      if (row.fields[i].type === 'submit') {
        submitButtonInRow = true;
        break;
      }
    }

    // Process fields in this row
    for (let i = 0; i < row.fields.length; i++) {
      const field = row.fields[i];

      // If we encounter a page break
      if (field.type === 'page_break') {
        pageBreakInRow = true;

        // If we've already started the row, close it
        if (rowStarted) {
          currentRowHtml += `</div>`;
          rowStarted = false;

          // Add the row's HTML so far to the current page
          html += currentRowHtml;
          currentRowHtml = '';
        }

        // Add navigation buttons and close current page
        if (isPageStarted) {
          // Add next/prev buttons for the current page
          if (currentPage === 1) {
            html += `<div class="navigation-buttons first-page">
                      <button type="button" class="form-btn next-btn" data-page="${currentPage + 1}">Next</button>
                    </div>`;
          } else {
            html += `<div class="navigation-buttons">
                      <button type="button" class="form-btn prev-btn" data-page="${currentPage - 1}">Previous</button>
                      <button type="button" class="form-btn next-btn" data-page="${currentPage + 1}">Next</button>
                    </div>`;
          }

          html += `</div>`;
          isPageStarted = false;
        }

        // Increment page counter
        totalPages++;
        currentPage++;

        // Start a new page
        html += `<div class="page" data-page="${currentPage}">`;
        isPageStarted = true;

        continue; // Skip further processing for this field
      }

      // If this is the first field after a page break or the first field in the row
      if (!rowStarted) {
        currentRowHtml += `<div class="form-row ${row.layout}" id="${row.id}">`;
        rowStarted = true;
      }

      // Add the field to the current row HTML
      currentRowHtml += renderField(field);
    }

    // If we have a row that's still open, close it
    if (rowStarted) {
      currentRowHtml += `</div>`;
    }

    // Add any remaining row HTML to the current page
    html += currentRowHtml;
  }

  // Function to process a standalone field (not in a row)
  function processField(field) {
    if (field.type === 'page_break') {
      // Close the current page
      if (isPageStarted) {
        // Add next/prev buttons for the current page
        if (currentPage === 1) {
          html += `<div class="navigation-buttons first-page">
                    <button type="button" class="form-btn next-btn" data-page="${currentPage + 1}">Next</button>
                  </div>`;
        } else {
          html += `<div class="navigation-buttons">
                    <button type="button" class="form-btn prev-btn" data-page="${currentPage - 1}">Previous</button>
                    <button type="button" class="form-btn next-btn" data-page="${currentPage + 1}">Next</button>
                  </div>`;
        }

        html += `</div>`;
        isPageStarted = false;
      }

      // Increment page counter
      totalPages++;
      currentPage++;

      // Start a new page
      html += `<div class="page" data-page="${currentPage}">`;
      isPageStarted = true;
    } else {
      // Render any other type of field
      html += renderField(field);
    }
  }

  // Helper function to create label with required indicator if needed
  function getLabel(field) {
    const isRequired = field.required === true || field.required === "true";
    let labelClass = isRequired ? 'required-field-label' : '';
    let requiredIndicator = isRequired ? '<span class="required-indicator">*</span>' : '';

    return `<label for="${field.id}" class="${labelClass}">${field.label}${requiredIndicator}</label>`;
  }

  // Function to render a single field based on its type
  function renderField(field) {
    let fieldHtml = '';
    let fieldAttributes = '';

    if (field.required === true || field.required === "true") {
      fieldAttributes += ' required';
    }

    // Add field ID for conditional logic
    const fieldId = field.uniqueId || field.id;
    fieldAttributes += ` data-field-id="${fieldId}"`;

    // Add conditional logic attributes
    if (field.conditionalLogic && field.conditionalLogic.enabled) {
      fieldAttributes += ` data-conditional-logic='${JSON.stringify(field.conditionalLogic)}'`;
    }

    // Wrap field in conditional container if needed
    const hasConditionalLogic = field.conditionalLogic && field.conditionalLogic.enabled;
    const conditionalClass = hasConditionalLogic ? 'conditional-field' : '';
    const conditionalAttributes = hasConditionalLogic ?
      `data-conditional-logic='${JSON.stringify(field.conditionalLogic)}' data-field-id="${field.uniqueId || field.id}"` : '';

    switch (field.type) {
      case 'image':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            <label for="${field.id}">${field.label}</label>
            <div class="${field.className}">
              <img src="${field.src}" width="${field.width}" height="${field.height}" alt="${field.label}">
            </div>
          </div>
        `;
        break;

      case 'hidden':
        fieldHtml += `
            <input id="${field.id}" type="${field.type}" name="${field.name}" placeholder="${field.placeholder}" class="${field.className} ${conditionalClass}" value="${field.value || ''}"${fieldAttributes} ${conditionalAttributes}>
        `;
        break;

      case 'textarea':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
           ${getLabel(field)}
            <textarea id="${field.id}" name="${field.name}" placeholder="${field.placeholder || ''}" class="${field.className}" data-field-id="${fieldId}"${fieldAttributes}>${field.value || ''}</textarea>
          </div>
        `;
        break;

      case 'file':
        fieldHtml += `
          <div class="text-fields">
           ${getLabel(field)}
            <input id="${field.id}" type="${field.type}" name="${field.name}" class="${field.className}"${fieldAttributes}>
          </div>
        `;
        break;

      case 'heading':
        fieldHtml += `
            <div class="text-fields">
              <${field.headingtype} class="${field.className}">${field.content}</${field.headingtype}>
            </div>
          `;
        break;

      case 'label':
        fieldHtml += `
              <div class="text-fields">
                <label for="${field.id}" class="${field.className}">${field.content}</label>
              </div>
            `;
        break;
      case 'link':
        fieldHtml += `
              <div class="simple-form-file">
                ${getLabel(field)}
                <a id="${field.id}" href="${field.href}" target="${field.target}" class="${field.className}">${field.content}</a>
              </div>
            `;
        break;
      case 'video':
        fieldHtml += `
              <div class="text-fields">
                <video
                  id="${field.id}"
                  width="${field.width}"
                  height="${field.height}"
                  controls
                  src="${field.src}"
                  class="${field.className}"
                >
                  ${field.content}
                </video>
              </div>
            `;
        break;
      case 'audio':
        fieldHtml += `
              <div class="text-fields">
                <audio
                  id="${field.id}"
                  width="${field.width}"
                  height="${field.height}"
                  controls
                  src="${field.src}"
                  class="${field.className}"
                >
                  ${field.content}
                </audio>
              </div>
            `;
        break;

      case 'iframe':
        fieldHtml += `
              <div class="text-fields">
                <iframe
                  id="${field.id}"
                  width="${field.width}"
                  height="${field.height}"
                  src="${field.src}"
                  class="${field.className}"
                >
                  ${field.content}
                </iframe>
              </div>
            `;
        break;

      case 'esignature':
        fieldHtml += `
          <div class="text-fields">
            ${getLabel(field)}
            <div class="signature-panel">
              <canvas id="${field.id}" class="signature-canvas" width="300" height="150"></canvas>
              <input type="hidden" name="${field.name}" class="signature-data" data-canvas-id="${field.id}">
              <button type="button" class="reset-signature form-btn" data-canvas-id="${field.id}">Reset Signature</button>
            </div>
          </div>
        `;
        break;

      // Never change the class name from editor sf-turnstile-container 
      case 'cloudflare':
        fieldHtml += `
          <div class="text-fields">
              <div class="${field.className}" id="${field.id}" data-sitekey="${field.sitekey}" data-theme="${field.theme}"   data-submit-button="${field.btnaccess}" data-size="${field.size}" data-error="${field.error}">
              </div>
          </div>
        `;
        break;

      case 'customcaptcha':
        // Generate two random values and an operator
        const value1 = Math.floor(Math.random() * 10) + 1;
        const value2 = Math.floor(Math.random() * 10) + 1;
        const operators = ['+', '-', '*'];
        const operator = operators[Math.floor(Math.random() * operators.length)];

        // Calculate the correct answer
        let correctAnswer;
        switch (operator) {
          case '+': correctAnswer = value1 + value2; break;
          case '-': correctAnswer = value1 - value2; break;
          case '*': correctAnswer = value1 * value2; break;
          default: break;
        }

        fieldHtml += `
          <div class="text-fields">
            ${getLabel(field)}
            <div>
              <span class="question-${field.className}">${value1} ${operator} ${value2} = </span>
              <input id="${field.id}" type="text" name="${field.name}" placeholder="Your Answer" class="${field.className}" required>
              <input type="hidden" name="${field.name}_correct" value="${correctAnswer}">
            </div>
          </div>
        `;
        break;

      case 'checkbox':
        const isRequired = field.required === true || field.required === "true";
        let requiredIndicator = isRequired ? '<span class="required-indicator">*</span>' : '';

        if (field.toggle === 'true') {
          // Render toggle checkbox
          fieldHtml += `
            <div class="simple-form-checkbox-toggle ${conditionalClass}" ${conditionalAttributes}>
              <label class="switch-label ${isRequired ? 'required-field-label' : ''}">${field.label}${requiredIndicator}
                <input type="checkbox" id="${field.id}" name="${field.name}" class="switch-input ${field.className}" data-field-id="${fieldId}"${fieldAttributes}>
                <span class="slider round"></span>
              </label>
            </div>`;
        } else {
          // Render standard checkboxes
          fieldHtml += `
            <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
              <label class="${isRequired ? 'required-field-label' : ''}">${field.label}${requiredIndicator}</label>`;
          field.options.forEach((option) => {
            fieldHtml += `
              <div class="text-fields-insider">
                <input type="checkbox" id="${option.value}" name="${field.name}" value="${option.value}" data-field-id="${fieldId}"${fieldAttributes}>
                <label for="${option.value}">${option.label}</label>
              </div>`;
          });
          fieldHtml += `
            </div>`;
        }
        break;

      case 'radio':
        const radioIsRequired = field.required === true || field.required === "true";
        let radioRequiredIndicator = radioIsRequired ? '<span class="required-indicator">*</span>' : '';

        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            <label class="${radioIsRequired ? 'required-field-label' : ''}">${field.label}${radioRequiredIndicator}</label>`;
        field.options.forEach((option) => {
          fieldHtml += `
            <div class="text-fields-insider">
              <input
                type="radio"
                id="${option.value}"
                name="${field.name}"
                value="${option.value}"
                ${field.value === option.value ? 'checked' : ''}
                data-field-id="${fieldId}"
                ${fieldAttributes}
              />
              <label for="${option.value}">${option.label}</label>
            </div>`;
        });
        fieldHtml += `
          </div>`;
        break;

      case 'select':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <select id="${field.id}" name="${field.name}" data-field-id="${fieldId}"${fieldAttributes}>`;
        field.options.forEach((option) => {
          fieldHtml += `
            <option value="${option.value}" ${field.value === option.value ? 'selected' : ''}>${option.label}</option>
          `;
        });
        fieldHtml += `
            </select>
          </div>
        `;
        break;

      case 'submit':
        fieldHtml += `
          <div class="text-fields">
            <input id="${field.id}" type="${field.type}" name="${field.name}" class="form-btn submit-btn ${field.className}" value="${field.value || 'Submit'}"${fieldAttributes}>
          </div>
        `;
        hasSubmitButton = true;
        break;

      case 'number':
        fieldHtml += `
          <div class="text-fields">
           ${getLabel(field)}
            <input id="${field.id}" type="${field.type}" name="${field.name}" min="${field.min}" max="${field.max}"  placeholder="${field.placeholder || ''}" class="${field.className}" value="${field.value || ''}"${fieldAttributes}>
          </div>
        `;
        break;

      // Handle standard input types
      case 'text':
      case 'number':
      case 'email':
      case 'button':
      case 'color':
      case 'date':
      case 'datetime-local':
      case 'month':
      case 'password':
      case 'range':
      case 'reset':
      case 'search':
      case 'tel':
      case 'time':
      case 'url':
        fieldHtml += `
          <div class="text-fields ${conditionalClass}" ${conditionalAttributes}>
            ${getLabel(field)}
            <input id="${field.id}" type="${field.type}" name="${field.name}" placeholder="${field.placeholder || ''}" class="${field.className}" value="${field.value || ''}" data-field-id="${fieldId}"${fieldAttributes}>
          </div>
        `;
        break;

      default:
        break;
    }

    return fieldHtml;
  }

  // Function to get theme-specific styles - mainly its used here but maxium style maintain from frontend.scss 
  function getThemeStyles(theme) {
    // Check if it's a custom theme
    if (theme && theme.startsWith('custom-')) {
      return getCustomThemeStyles(theme);
    }

    switch (theme) {
      case 'theme-1':
        return `
          <style>
            .simple_form_content.theme-1 {
              background-color: #ffffff;
              color: #333333;
            }
          </style>
        `;

      case 'theme-2':
        return `
          <style>
            .simple_form_content.theme-2 {
              background-color: #f8f9fa;
              color: #212529;
              padding: 25px;
              border-radius: 12px;
            }

            .submit-button{
              border: 1px solid #333333;
            }

          </style>
        `;
      case 'theme-3':
        return `
          <style>
            .simple_form_content.theme-3 {
              background-color:#2c3e50;
              color: #ecf0f1;
              padding: 30px;
              border-radius: 8px;
            }

            .submit-button{
              border: 1px solid  #ecf0f1;
            }
          </style>
        `;
      case 'theme-4':
        return `
          <style>
            .simple_form_content.theme-4 {
              background-color: #f5f6fa;
              color: #2c3e50;
              padding: 30px;
              border-radius: 12px;
            }

            .submit-button{
              border: 1px solid #2c3e50;
            }
          </style>
        `;
      case 'theme-5':
        return `
          <style>
            .simple_form_content.theme-5 {
              background-color: #1e1e2f;
              color: #ecf0f1;
               padding: 30px;
              border-radius: 12px;
           
            }

            .submit-button{
              border: 1px solid #ecf0f1;
            }
          </style>
        `;
      case 'theme-6':
        return `
          <style>
            .simple_form_content.theme-6 {
              background-color: #e6eef9;
              color: #2d3436;
              border-radius: 12px;
              font-weight: 600;
              border: none;
              padding: 12px 24px;
             
              transition: all 0.3s ease;
            }

            .submit-button{
              border: 1px solid #2d3436;
            }
          </style>
        `;
      case 'theme-7':
        return `
          <style>
            .simple_form_content.theme-7 {
              background-color: #121212;
              color: #ffffff;
              padding: 30px;
              border-radius: 16px;
              backdrop-filter: blur(10px);
             
              border: 1px solid rgba(255, 255, 255, 0.05);
            }

            .submit-button{
              border: 1px solid #ffffff;
            }
          </style>
        `;

      default:
        return '';
    }
  }

  // Function to get custom theme styles
  function getCustomThemeStyles(themeId) {
    // If already injected into <head>, nothing to do
    if (document.querySelector(`style[data-theme-id="${themeId}"]`)) {
      return '';
    }

    // Try to get custom theme from global variable (set by PHP via wp_add_inline_script)
    if (window.simpleform_custom_themes && window.simpleform_custom_themes[themeId]) {
      const customTheme = window.simpleform_custom_themes[themeId];
      const styleEl = document.createElement('style');
      styleEl.setAttribute('data-theme-id', themeId);
      styleEl.textContent = customTheme.css_styles;
      document.head.appendChild(styleEl);
      return ''; // Don't return inline — injected into head above
    }

    // Fallback: Load custom theme via AJAX (for backward compatibility)
    loadCustomThemeAsync(themeId);
    return '';
  }

  // Async function to load custom theme styles
  function loadCustomThemeAsync(themeId) {
    fetch(front_end_data.admin_ajax, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        action: 'simpleform_get_custom_theme_styles',
        theme_id: themeId,
        nonce: front_end_data.nonce
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data.css_styles) {
          // Inject the custom theme styles into the page
          const styleElement = document.createElement('style');
          styleElement.setAttribute('data-theme-id', themeId);
          styleElement.textContent = data.data.css_styles;
          document.head.appendChild(styleElement);
        }
      })
      .catch(error => {
        console.error('Error loading custom theme:', error);
      });
  }
}



/**
 * Load Form and new page break add.
 * Load Form with page break
 */

document.addEventListener('DOMContentLoaded', function () {
  var formContainers = document.querySelectorAll('.simple_form_container');

  formContainers.forEach(function (container) {
    var formId = container.getAttribute('data-form-id');
    var formTheme = container.getAttribute('data-form-theme');
    var formPosition = container.getAttribute('data-position') || 'inline';
    // console.log(formTheme)
    var nonce = container.getAttribute('data-nonce');
    var cf7 = container.getAttribute('data-cf7');
    // var markupId = container.querySelector('.simple_form_loader').id;
    var markupId = '';
    var simpleFormLoader = container.querySelector('.simple_form_loader');
    if (simpleFormLoader) {
      var markupId = simpleFormLoader.id;
    }

    var isFloatingForm = formPosition !== 'inline';

    var submitBtn = container.querySelector('.submit-button');

    var isThemeActiveInForm = container.querySelector('.floating-form-container') !== null;
    // console.log(isThemeActiveInForm);

    var theme = isThemeActiveInForm ? 'theme-off' : formTheme;  // 1 to 7
    // var theme = isThemeActiveInForm ? 'theme-off' : 'theme-7';  // 1 to 7

    console.log('Form Theme:', theme);

    if (isFloatingForm) {
      var toggleBtn = container.querySelector('.simple_form_toggle');
      var floatingContainer = container.querySelector('.simple_form_floating_container');
      var closeBtn = container.querySelector('.simple_form_close');
      var expandBtn = container.querySelector('.simple_form_expand_btn');
      var isExpanded = false;

      if (toggleBtn && floatingContainer) {
        toggleBtn.addEventListener('click', function () {
          // First set display to block (but still invisible due to opacity: 0)
          floatingContainer.style.display = 'block';

          toggleBtn.style.display = 'none';

          // Force a reflow to ensure the display change takes effect before adding the visible class
          void floatingContainer.offsetWidth;

          // Then add the visible class to trigger the transition
          setTimeout(function () {
            floatingContainer.classList.add('visible');
          }, 10);
        });
      }

      if (closeBtn && floatingContainer) {
        closeBtn.addEventListener('click', function () {
          // First remove the visible class to trigger the fade out
          floatingContainer.classList.remove('visible');

          // Then hide after transition completes
          setTimeout(function () {
            floatingContainer.style.display = 'none';
            toggleBtn.style.display = 'flex';
          }, 300); // Match the CSS transition duration
        });
      }

      if (expandBtn && floatingContainer) {
        // Set the initial expand icon
        expandBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"></path><path d="M9 21H3v-6"></path><path d="M21 3l-7 7"></path><path d="M3 21l7-7"></path></svg>';

        expandBtn.addEventListener('click', function () {
          isExpanded = !isExpanded;

          if (isExpanded) {
            // Expand the form
            floatingContainer.classList.add('expanded');
            // Change to minimize icon
            expandBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3"></path><path d="M21 8h-3a2 2 0 0 1-2-2V3"></path><path d="M3 16h3a2 2 0 0 1 2 2v3"></path><path d="M16 21v-3a2 2 0 0 1 2-2h3"></path></svg>';
          } else {
            // Restore original size
            floatingContainer.classList.remove('expanded');
            // Restore the expand icon
            expandBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"></path><path d="M9 21H3v-6"></path><path d="M21 3l-7 7"></path><path d="M3 21l7-7"></path></svg>';
          }
        });
      }
    }


    fetch(front_end_data.admin_ajax + '?action=simpleform_table_html&nonce=' + nonce + '&id=' + formId + '&iscf7=' + cf7)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(function (data) {
        // console.log(data.data.cf7_render)
        if (cf7 === 'false') {
          const code = document.getElementById(markupId);
          const formData = data.data.table_settings;
          const isPro = front_end_data.isProActive;
          const renderedFormHTML = generateRenderedForm(formData, isPro, theme);
          code.innerHTML = renderedFormHTML;

          // Add navigation functionality
          addNavigationFunctionality(container);

          // Add conditional logic functionality
          addConditionalLogicFunctionality(container);

          // Add progress indicator functionality
          addProgressIndicatorFunctionality(container);

          /**
          * Initilize all required function for fields 
          */

          //Signature 
          canvasSignature(code);

          // Set up reset button click handlers
          setupResetButtons(code);

          // Initialize the form slider : Keep it below of all 
          initializeFormSlider(code, submitBtn);

        }
        // No need to render through JS as direct shortcode used to render 
        /* else{
          const code = document.getElementById(markupId);
          const formData = data.data.cf7_render;
          code.innerHTML = formData;
        } */

      })
      .catch(function (error) {
        // console.log('SForm', formId, 'Error:', error.message);
      });
  });
});



// Animention task ------------

// Function to apply attention-grabbing animation
function applyAttentionAnimation(toggleBtn) {
  // Add animation class
  toggleBtn.classList.add('simple_form_attention');

  // Remove animation class after animation completes
  setTimeout(() => {
    toggleBtn.classList.remove('simple_form_attention');
  }, 2000); // Animation duration + a bit extra
}

// Function to apply spinning animation on click
function applySpinAnimation(toggleBtn) {
  toggleBtn.classList.add('simple_form_spin');
}

// Add the animation logic to your existing code
document.addEventListener('DOMContentLoaded', function () {
  var formContainers = document.querySelectorAll('.simple_form_container');

  formContainers.forEach(function (container) {
    var formPosition = container.getAttribute('data-position') || 'inline';
    var isFloatingForm = formPosition !== 'inline';

    if (isFloatingForm) {
      var toggleBtn = container.querySelector('.simple_form_toggle');
      var floatingContainer = container.querySelector('.simple_form_floating_container');

      if (toggleBtn && floatingContainer) {
        // Set up attention animation interval
        setInterval(function () {
          applyAttentionAnimation(toggleBtn);
        }, 10000); // Run every 10 seconds

        // Run it once after 3 seconds on page load
        setTimeout(function () {
          applyAttentionAnimation(toggleBtn);
        }, 3000);

        // Add click handler with spinning animation
        toggleBtn.addEventListener('click', function () {
          // Add spinning animation
          applySpinAnimation(toggleBtn);

          // First set display to block
          floatingContainer.style.display = 'block';

          // Force a reflow
          void floatingContainer.offsetWidth;

          // Add visible class to trigger transition
          setTimeout(function () {
            floatingContainer.classList.add('visible');

            // Remove spinning animation once form is visible
            setTimeout(function () {
              toggleBtn.classList.remove('simple_form_spin');
            }, 500);
          }, 10);
        });
      }
    }
  });
});

// Animention task Emd


/**
 * Signature 
 */

function canvasSignature(formContainer) {
  var pages = formContainer.querySelectorAll('.page');

  pages.forEach(function (page) {
    const signatureCanvases = page.querySelectorAll('.signature-canvas');
    signatureCanvases.forEach(function (canvas) {
      const context = canvas.getContext('2d');
      let isDrawing = false;

      // Handle mouse events
      canvas.addEventListener('mousedown', function (e) {
        isDrawing = true;
        drawStart(e, context);
      });

      canvas.addEventListener('mousemove', function (e) {
        if (isDrawing) {
          draw(e, context);
        }
      });

      canvas.addEventListener('mouseup', function () {
        if (isDrawing) {
          isDrawing = false;
          drawEnd(context);
        }
      });

      canvas.addEventListener('mouseout', function () {
        if (isDrawing) {
          isDrawing = false;
          drawEnd(context);
        }
      });

      // Handle touch events
      canvas.addEventListener('touchstart', function (e) {
        e.preventDefault();
        isDrawing = true;
        const touch = e.touches[0];
        const simulatedEvent = new MouseEvent('mousedown', {
          clientX: touch.clientX,
          clientY: touch.clientY
        });
        canvas.dispatchEvent(simulatedEvent);
      });

      canvas.addEventListener('touchmove', function (e) {
        e.preventDefault();
        if (isDrawing) {
          const touch = e.touches[0];
          const simulatedEvent = new MouseEvent('mousemove', {
            clientX: touch.clientX,
            clientY: touch.clientY
          });
          canvas.dispatchEvent(simulatedEvent);
        }
      });

      canvas.addEventListener('touchend', function () {
        e.preventDefault();
        if (isDrawing) {
          isDrawing = false;
          drawEnd(context);
        }
      });

      canvas.addEventListener('touchcancel', function () {
        e.preventDefault();
        if (isDrawing) {
          isDrawing = false;
          drawEnd(context);
        }
      });
    });
  });
}


function drawStart(e, context) {
  context.beginPath();
  draw(e, context);
}

function drawEnd(context) {
  context.closePath();
}

function draw(e, context) {
  const rect = e.target.getBoundingClientRect();
  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;

  context.lineWidth = 2;
  context.lineCap = 'round';

  context.lineTo(mouseX, mouseY);
  context.stroke();
  context.moveTo(mouseX, mouseY);
}


/**
 * 
 * Reset Canvas
 */

function setupResetButtons(formContainer) {
  const resetButtons = formContainer.querySelectorAll('.reset-signature');
  // console.log(resetButtons)
  resetButtons.forEach(function (button) {
    button.addEventListener('click', function () {
      const canvasId = button.getAttribute('data-canvas-id');
      resetSignatureCanvas(canvasId);
    });
  });
}

function resetSignatureCanvas(canvasId) {

  const canvas = document.getElementById(canvasId);
  const context = canvas.getContext('2d');

  // Clear the canvas
  context.clearRect(0, 0, canvas.width, canvas.height);

  // Additional code for resetting any other related state or variables
}

/**
 * 
 Slider form for next and prev page
 */
function initializeFormSlider(formContainer, submitBtn) {

  var pages = formContainer.querySelectorAll('.page');
  var currentPage = 1;

  // console.log(formContainer)
  // console.log("initializeFormSlider is running!");

  function showPage(pageNumber) {
    pages.forEach(function (page) {
      page.style.display = 'none';
    });

    var currentPageElement = formContainer.querySelector('.page[data-page="' + pageNumber + '"]');
    if (currentPageElement) {
      currentPageElement.style.display = 'block';
    }

    // Toggle the visibility of the submit button based on the presence of the next button.
    var isNext = currentPageElement.querySelector('.next-btn') !== null;

    if (submitBtn) {
      // submitBtn.style.display = isNext ? 'none' : 'inline-block';
      submitBtn.style.display = isNext ? 'none' : 'flex';
    } else {
      // console.log("Button not found")
    }
  }

  /**
   * Page next and prev button visibility
   */
  function updateButtonsVisibility() {
    var prevButton = formContainer.querySelector('.prev-btn');
    var nextButton = formContainer.querySelector('.next-btn');

    if (currentPage === 1) {
      prevButton.style.display = 'none';
    } else {
      prevButton.style.display = 'inline-block';
    }

    if (currentPage === pages.length) {
      nextButton.style.display = 'none';
    } else {
      nextButton.style.display = 'inline-block';
    }
  }

  // Initial setup
  showPage(currentPage);
  updateButtonsVisibility();

  // Event listeners for next and previous buttons
  formContainer.addEventListener('click', function (event) {
    var target = event.target;

    if (target.classList.contains('next-btn')) {
      // Do not proceed if required fields are not filled
      if (!validateRequiredFields(currentPage)) {
        return;
      }
      //IF all ok move next 
      currentPage++;

    } else if (target.classList.contains('prev-btn')) {
      currentPage--;
    }
    showPage(currentPage);
    updateButtonsVisibility();
  });


  /**
   * Validation of next button move
   */
  function validateRequiredFields(pageNumber) {
    var currentPageElement = formContainer.querySelector('.page[data-page="' + pageNumber + '"]');
    var requiredFields = currentPageElement.querySelectorAll('[required]');
    var isValid = true;

    requiredFields.forEach(function (field) {
      if (field.value.trim() === '') {
        field.style.border = '1px solid red';
        isValid = false;
      } else {
        field.style.border = '';
      }
    });

    var errorMessage = currentPageElement.querySelector('.blank-filled-error-message');

    if (!isValid) {
      // Display an error message below the "text-fields" div
      if (!errorMessage) {
        errorMessage = document.createElement('div');
        errorMessage.className = 'blank-filled-error-message';
        errorMessage.style.color = 'red';
        errorMessage.textContent = 'Please fill out all required fields.';
        currentPageElement.querySelector('.text-fields').appendChild(errorMessage);
      }
    } else {
      // Remove the error message if fields are valid
      if (errorMessage) {
        errorMessage.remove();
      }
    }

    return isValid;
  }

}

// Modal --------------------
// Custom modal implementation to replace SweetAlert
// Add this to your existing JavaScript file

function showCustomModal(title, message, duration = 3000) {
  // Create modal container
  const modalOverlay = document.createElement('div');
  modalOverlay.className = 'custom-modal-overlay';

  // Create modal content
  const modalContent = document.createElement('div');
  modalContent.className = 'custom-modal';

  // Create header with title
  const modalHeader = document.createElement('div');
  modalHeader.className = 'custom-modal-header';

  const modalTitle = document.createElement('h3');
  modalTitle.textContent = title;
  modalHeader.appendChild(modalTitle);

  // Create message content
  const modalBody = document.createElement('div');
  modalBody.className = 'custom-modal-body';
  modalBody.innerHTML = message;

  // Create progress bar
  const progressBar = document.createElement('div');
  progressBar.className = 'custom-modal-progress';

  const progressIndicator = document.createElement('div');
  progressIndicator.className = 'custom-modal-progress-indicator';
  progressBar.appendChild(progressIndicator);

  // Assemble the modal
  modalContent.appendChild(modalHeader);
  modalContent.appendChild(modalBody);
  modalContent.appendChild(progressBar);
  modalOverlay.appendChild(modalContent);

  // Add to document
  document.body.appendChild(modalOverlay);

  // Show modal with animation
  setTimeout(() => {
    modalOverlay.classList.add('visible');
    progressIndicator.style.transition = `width ${duration}ms linear`;
    progressIndicator.style.width = '100%';
  }, 10);

  // Auto close after duration
  setTimeout(() => {
    modalOverlay.classList.remove('visible');
    setTimeout(() => {
      document.body.removeChild(modalOverlay);
    }, 300); // Wait for fade out animation
  }, duration);

  // Allow clicking outside to close
  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) {
      modalOverlay.classList.remove('visible');
      setTimeout(() => {
        document.body.removeChild(modalOverlay);
      }, 300);
    }
  });
}


/**
 * Submit Form data get and store in DB and other form submission task here
 */

window.addEventListener('load', function () {
  var formContainers = document.querySelectorAll('.simple_form_container');
  const formContent = document.querySelector('.form-content');

  formContainers.forEach(function (formContainer) {
    var formId = formContainer.dataset.formId;
    var nonce = formContainer.dataset.nonce;

    // Find the submit button within the current form container
    var submitButton = formContainer.querySelector('.submit-button');


    if (!submitButton) {
      return;
    }


    // Store the original button text
    var originalButtonText = submitButton.innerText;

    // Create a div for displaying error messages
    var errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.style.color = 'red';
    formContainer.appendChild(errorDiv);


    submitButton.addEventListener('click', function (event) {

      // Check if this is an auth form (login/registration/comment)
      // If so, let the auth-forms-handler.js handle it
      // But only if pro is active
      if (typeof front_end_data !== 'undefined' && 
          typeof front_end_data.auth_form_ids !== 'undefined' &&
          typeof front_end_data.isProActive !== 'undefined' && 
          front_end_data.isProActive) {
        const authFormIds = front_end_data.auth_form_ids;
        const currentFormId = parseInt(formId);
        
        // Check if this form is configured as an auth form
        if (currentFormId === authFormIds.wp_login || 
            currentFormId === authFormIds.wp_registration || 
            currentFormId === authFormIds.wc_login || 
            currentFormId === authFormIds.wc_registration || 
            currentFormId === authFormIds.comment) {
          // console.log('Auth form detected - skipping regular form submission handler');
          // Don't prevent default here - let auth-forms-handler.js handle it
          return;
        }
      }

      // Prevent the default form submission behavior
      event.preventDefault();

      // Change button text to "Sending..."
      submitButton.innerText = 'Sending...';

      // Get all form elements within the form
      var formElements = formContainer.querySelectorAll('input, select, textarea');

      // Create an object to store the form data
      var formDataObject = {};

      // Create a flag to track if there are any required fields that are empty
      var hasEmptyRequiredFields = false;
      var errorMessage = '';

      formElements.forEach(function (element) {
        // Check if the element has the "simple-form-checkbox-toggle" class
        var isToggleCheckbox = element.closest('.simple-form-checkbox-toggle') !== null;

        // Handle checkboxes with the "simple-form-checkbox-toggle" class
        if (isToggleCheckbox && element.type === 'checkbox') {
          formDataObject[element.name] = element.checked ? 'on' : 'off';
        } else if (element.type === 'radio') {
          // Only store radio button value if it's checked
          if (element.checked) {
            formDataObject[element.name] = element.value;
          }
        } else if (element.type === 'checkbox') {
          // Handle regular checkboxes - only store if checked
          if (element.checked) {
            // If multiple checkboxes have the same name, store as array
            if (formDataObject[element.name]) {
              // Convert to array if not already
              if (!Array.isArray(formDataObject[element.name])) {
                formDataObject[element.name] = [formDataObject[element.name]];
              }
              formDataObject[element.name].push(element.value);
            } else {
              formDataObject[element.name] = element.value;
            }
          }
        } else {
          // Handle all other input types (text, email, etc.)
          formDataObject[element.name] = element.value;
        }

        // Check if the field is required and empty
        if (element.hasAttribute('required') && formDataObject[element.name].trim() === '') {
          // Add a red border to indicate the required field is empty
          element.style.border = '1px solid red';
          hasEmptyRequiredFields = true;
        } else {
          // Remove any red border if the field is not empty
          element.style.border = '';
        }

        // Add email validation
        if (element.type === 'email' && !isValidEmail(element.value)) {
          // Add a red border to indicate an invalid email
          element.style.border = '1px solid red';
          errorMessage = 'Email is not correct.';
          hasEmptyRequiredFields = true;
        }

        // Add number validation
        if (element.type === 'number') {
          const min = element.getAttribute('min');
          const max = element.getAttribute('max');

          if (!isValidNumber(element.value, min, max)) {
            // Add a red border to indicate an invalid number
            element.style.border = '1px solid red';

            if (element.value === '') {
              errorMessage = 'Please fillup required field and maintain its format.';
            } else if (!(/^\d+$/.test(element.value))) {
              errorMessage = 'Please enter a valid number (digits only).';
            } else if (min !== '' || max !== '') {
              if (min !== '' && max !== '') {
                errorMessage = `Number must be between ${min} and ${max}.`;
              } else if (min !== '') {
                errorMessage = `Number must be at least ${min}.`;
              } else {
                errorMessage = `Number must not exceed ${max}.`;
              }
            }

            hasEmptyRequiredFields = true;
          }
        }

        //custom captch
        if (element.name.endsWith('_correct')) {
          const captchaFieldName = element.name.replace('_correct', '');
          const captchaUserAnswer = formDataObject[captchaFieldName];
          const captchaCorrectAnswer = element.value;

          if (captchaUserAnswer !== captchaCorrectAnswer) {
            // Display an error message for the custom captcha
            errorMessage = 'Wrong captcha, please fill in the correct answer.';
            hasEmptyRequiredFields = true;
          }
        }

        //Canvas to image create to store the image in databse as a link
        if (element.classList.contains('signature-data')) {
          var canvasId = element.dataset.canvasId;
          var signatureCanvas = document.getElementById(canvasId);
          // console.log(signatureCanvas);
          // Capture signature data as a base64-encoded PNG image
          var signatureData = signatureCanvas.toDataURL('image/png');
          // console.log(signatureData)
          formDataObject[element.name] = encodeURIComponent(signatureData); // it keep the + before sending to network
          // console.log(formDataObject[element.name]);
        }

      });


      // If there are empty required fields, prevent form submission
      if (hasEmptyRequiredFields || errorMessage) {
        submitButton.innerText = originalButtonText;
        // alert('Please fill out all required fields.');
        errorDiv.textContent = errorMessage || 'Please fill out all required fields.';
        return;
      } else {
        // Clear the error message if there are no errors
        errorDiv.textContent = '';
      }

      // Create an AJAX request for the form submission
      var xhr = new XMLHttpRequest();
      xhr.open('POST', front_end_data.admin_ajax, true);
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');

      var data = "action=simpleform_get_submit_data" + "&nonce=" + encodeURIComponent(nonce) + "&id=" + encodeURIComponent(formId) + "&form_data=" + JSON.stringify(formDataObject) + "&current_page_url=" + encodeURIComponent(window.location.href);

      xhr.onreadystatechange = function () {
        if (xhr.readyState === 4) {

          // Set button text back to the original text
          submitButton.innerText = originalButtonText;

          if (xhr.status === 200) {
            var responseData = JSON.parse(xhr.responseText);

            // console.log('Response:', responseData);

            // Check if the form should be closed after submission

            var shouldCloseForm = formContainer.dataset.close === "true";

            if (responseData.success) {
              // Clear the form fields
              var form = formContainer.querySelector('.simple_form');
              if (form) {
                form.reset();

                // form.reset() doesn't clear canvas elements — reset all signature canvases manually
                form.querySelectorAll('.signature-canvas').forEach(function(canvas) {
                  var ctx = canvas.getContext('2d');
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                  // Also clear the associated hidden input
                  var hiddenInput = form.querySelector('.signature-data[data-canvas-id="' + canvas.id + '"]');
                  if (hiddenInput) hiddenInput.value = '';
                });
              }


              // Close the form if required
              if (shouldCloseForm) {
                formContent.classList.remove('active');
                formContent.style.height = '0';
              }


              //Rest captch and add new value 
              var captchaField = form.querySelector('[name^="custom-captcha-input"]');
              var captchaCorrectField = form.querySelector('[name^="custom-captcha-input-"][name$="_correct"]');
              // Generate a random operation and operands
              var captchaOperation = generateRandomOperation();
              // console.log(captchaOperation);
              // Update the UI with the new captcha operation
              var captchaSpan = form.querySelector('[class^="question-"]');

              if (captchaSpan) {
                captchaSpan.textContent = `${captchaOperation.operand1} ${captchaOperation.operation} ${captchaOperation.operand2} =`;
                // Set the correct value
                captchaCorrectField.value = eval(`${captchaOperation.operand1} ${captchaOperation.operation} ${captchaOperation.operand2}`);
                // Clear the user's input
                captchaField.value = '';
              }


              /**
               * WhatsApp redirection
               */
              if (responseData.data && responseData.data.whatsapp_settings) {
                handleWhatsAppRedirection(responseData.data.whatsapp_settings, formDataObject, formId);
              }

              // Track form success analytics
              trackFormEvent(formId, 'form_success', {
                has_redirect: responseData.data && responseData.data.redirect_url && responseData.data.redirect_url !== '',
                submission_time: new Date().toISOString()
              });

              // Check for thank you page redirect
              if (responseData.data && responseData.data.redirect_url && responseData.data.redirect_url !== '') {
                // Track redirect analytics
                trackFormEvent(formId, 'form_redirect', {
                  redirect_url: responseData.data.redirect_url,
                  open_new_tab: responseData.data.open_new_tab || false
                });

                // Check if redirect should open in new tab
                if (responseData.data.open_new_tab) {
                  // Open in new tab
                  window.open(responseData.data.redirect_url, '_blank');

                  // Show success modal since we're not redirecting the current page
                  showCustomModal(
                    'Thanks for your submission',
                    'We will contact you soon <br> Have a nice day.',
                    3000 // Duration in milliseconds (3 seconds)
                  );
                } else {
                  // Redirect in current tab
                  window.location.href = responseData.data.redirect_url;
                  return; // Exit early to prevent showing the modal
                }
              }

              showCustomModal(
                'Thanks for your submission',
                'We will contact you soon <br> Have a nice day.',
                3000 // Duration in milliseconds (3 seconds)
              );

            } else {
              // Track form error analytics
              trackFormEvent(formId, 'form_error', {
                error_message: responseData.data ? responseData.data.message : 'Unknown error',
                error_time: new Date().toISOString()
              });

              // console.log(responseData.data.message);
              errorDiv.textContent = "Don't try it again! You silly human!"
            }

          } else {
            // Handle the error for this form
            // console.log('Form', formId, 'Error:', xhr.statusText);
          }
        }
      };

      xhr.send(data);
    });
  });
});


//after submit rest and add new vlaue to captch
function generateRandomOperation() {
  const operations = ['+', '-', '*'];
  // const operations = ['+', '-', '*', '/'];
  const operation = operations[Math.floor(Math.random() * operations.length)];

  let operand1, operand2;
  if (operation === '/') {
    operand2 = Math.floor(Math.random() * 9) + 1; // Ensure operand2 is not zero
    operand1 = operand2 * (Math.floor(Math.random() * 9) + 1);
  } else {
    operand1 = Math.floor(Math.random() * 10);
    operand2 = Math.floor(Math.random() * 10);
  }

  return { operation, operand1, operand2 };
}


// Helper function to validate email
function isValidEmail(email) {
  var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
}

// Helper function to validate number
function isValidNumber(value, min, max) {
  // First check if it matches your number pattern (digits only)
  var numberPattern = /^\d+$/;
  if (!numberPattern.test(value)) {
    return false;
  }

  // Convert to number for min/max comparison
  const numValue = parseFloat(value);

  // Check min constraint if present
  if (min !== undefined && min !== '' && numValue < parseFloat(min)) {
    return false;
  }

  // Check max constraint if present
  if (max !== undefined && max !== '' && numValue > parseFloat(max)) {
    return false;
  }

  return true;
}


function setCookie(name, value, days) {
  let expires = "";

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${name}=${value || ""}${expires}; path=/`;
}

function getCookie(name) {
  const cookieStr = decodeURIComponent(document.cookie);
  const cookies = cookieStr.split("; ");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];

    if (cookie.startsWith(`${name}=`)) {
      return cookie.substring(name.length + 1);
    }
  }
  return "";
}

function eraseCookie(name) {
  setCookie(name, "", -1);
}

// END ---------------------------------------------------


/**
 *  Floating form action 
 */
window.addEventListener('load', function () {
  // Get references to the WhatsApp icon and the form content
  const whatsappIcon = document.querySelector('.whatsapp-icon');
  const formContent = document.querySelector('.form-content');
  const closeButton = document.querySelector('.sf-close');
  const containerFloat = document.querySelector('.simple_form_container_floating');

  // Toggle the "active" class and adjust the height of the form content when the icon is clicked
  if (whatsappIcon) {
    whatsappIcon.addEventListener('click', function () {
      formContent.classList.toggle('active');
      adjustFormHeight(formContent);
    });
  }

  // Close the form when the close button is clicked
  if (closeButton) {
    closeButton.addEventListener('click', function () {
      formContent.classList.remove('active');
      formContent.style.height = '0';
    });
  }

  // Check if the form should be initially open

  if (containerFloat) {
    var OpenForm = containerFloat.dataset.open === "true";
    // console.log(containerFloat.dataset)
    if (OpenForm) {
      formContent.classList.add('active');
      adjustFormHeight(formContent);
    }
  }



  function adjustFormHeight(element) {
    const isActive = element.classList.contains('active');
    if (isActive) {
      // Calculate the full height of the form content
      // element.style.height = element.scrollHeight + 'px'; // prev used
      element.style.height = 'auto';
      element.style.maxHeight = '520px';

      if (containerFloat) {
        containerFloat.style.overflowY = 'auto';
        containerFloat.style.maxHeight = '465px';
      }

    } else {
      // Set the height back to 0 for collapsing effect
      element.style.height = '0';
    }
  }

});


// Function to restart the animation
function restartAnimation() {
  const whatsappIcon = document.getElementById('jumping-whatsapp');
  if (whatsappIcon) {
    whatsappIcon.style.animation = 'none';
    // Trigger reflow
    void whatsappIcon.offsetWidth;
    // Set animation duration to 5 seconds
    whatsappIcon.style.animation = 'jumpAnimation 0.9s ease-in-out';
  }

}

// Initially, trigger the animation 4-5 times with a delay
let initialAnimationCount = 0;
// Random value between 4 and 5
const maxInitialAnimationCount = Math.floor(Math.random() * 2) + 1;

function initialAnimations() {
  if (initialAnimationCount < maxInitialAnimationCount) {
    restartAnimation();
    initialAnimationCount++;
    setTimeout(initialAnimations, 200); // Adjust the delay as needed
  }
}

// Start initial animations
initialAnimations();

// After the initial animations, continue with the 10-second animation cycle
setInterval(restartAnimation, 3000); // Restart every

// END 

//Analytics tracking function — only runs when Pro is active AND analytics tracking is enabled in Settings
function trackFormEvent(formId, eventType, eventData = {}) {
  // Guard: Pro must be active AND admin must have enabled tracking in General Settings
  if ( ! front_end_data.analyticsEnabled ) {
    return;
  }

  // Get user information
  const userAgent = navigator.userAgent;
  const pageUrl = window.location.href;
  
  // Now simply get the page URL and user agent only 
  const data = new FormData();
  data.append('action', 'simpleform_track_event');
  data.append('nonce', front_end_data.nonce);
  data.append('form_id', formId);
  data.append('event_type', eventType);
  data.append('event_data', JSON.stringify(eventData));
  data.append('user_agent', userAgent);
  data.append('page_url', pageUrl);

  // Send analytics data
  fetch(front_end_data.admin_ajax, {
    method: 'POST',
    body: data
  }).catch(function (error) {
    console.log('Analytics tracking error:', error);
  });
}

// Track form view when form is loaded
document.addEventListener('DOMContentLoaded', function () {
  const formContainers = document.querySelectorAll('.simple_form_container');

  formContainers.forEach(function (container) {
    const formId = container.getAttribute('data-form-id');
    if (formId) {
      // Track form view
      trackFormEvent(formId, 'form_view', {
        form_theme: container.getAttribute('data-form-theme'),
        form_position: container.getAttribute('data-position') || 'inline'
      });
    }
  });
});

// Function to add navigation functionality for multistep forms
function addNavigationFunctionality(container) {
  const pages = container.querySelectorAll('.page');
  const nextButtons = container.querySelectorAll('.next-btn');
  const prevButtons = container.querySelectorAll('.prev-btn');

  if (pages.length <= 1) return; // No need for navigation if only one page

  // Show first page initially
  pages.forEach((page, index) => {
    page.style.display = index === 0 ? 'block' : 'none';
  });

  // Add click handlers for next buttons
  nextButtons.forEach(button => {
    button.addEventListener('click', function () {
      const targetPage = parseInt(this.getAttribute('data-page'));
      showPage(targetPage, pages, container);
    });
  });

  // Add click handlers for prev buttons
  prevButtons.forEach(button => {
    button.addEventListener('click', function () {
      const targetPage = parseInt(this.getAttribute('data-page'));
      showPage(targetPage, pages, container);
    });
  });
}

// Function to show a specific page
function showPage(pageNumber, pages, container) {
  pages.forEach((page, index) => {
    page.style.display = (index + 1) === pageNumber ? 'block' : 'none';
  });

  // Update progress indicator
  updateProgressIndicator(container, pageNumber);
}

// Function to add conditional logic functionality
function addConditionalLogicFunctionality(container) {
  const conditionalFields = container.querySelectorAll('[data-conditional-logic]');

  if (conditionalFields.length === 0) return;

  // console.log('Setting up conditional logic for', conditionalFields.length, 'fields');

  // Debug: List all fields with data-field-id
  const allFieldsWithId = container.querySelectorAll('[data-field-id]');
  /* console.log('All fields with data-field-id:', Array.from(allFieldsWithId).map(f => ({
    id: f.getAttribute('data-field-id'),
    tag: f.tagName,
    type: f.type || 'N/A',
    name: f.name || 'N/A'
  }))); */

  // Add change listeners to all form inputs
  const allInputs = container.querySelectorAll('input, select, textarea');
  // console.log('Found', allInputs.length, 'input elements');

  allInputs.forEach(input => {
    // Use both 'input' and 'change' events for real-time updates
    input.addEventListener('input', function () {
      // console.log('Input event triggered on:', input.getAttribute('data-field-id'), 'value:', input.value);
      evaluateConditionalLogic(container);
    });
    input.addEventListener('change', function () {
      // console.log('Change event triggered on:', input.getAttribute('data-field-id'), 'value:', input.value);
      evaluateConditionalLogic(container);
    });
    // Also listen for keyup for immediate feedback
    input.addEventListener('keyup', function () {
      evaluateConditionalLogic(container);
    });
  });

  // Initial evaluation
  setTimeout(() => {
    evaluateConditionalLogic(container);
  }, 100);
}

// Function to evaluate conditional logic for all fields
function evaluateConditionalLogic(container) {
  const conditionalFields = container.querySelectorAll('[data-conditional-logic]');

  conditionalFields.forEach(field => {
    try {
      const logicData = JSON.parse(field.getAttribute('data-conditional-logic'));

      // Skip if conditional logic is not enabled
      if (!logicData.enabled || logicData.enabled === 'false' || logicData.enabled === false) {
        return;
      }

      const shouldShow = evaluateConditions(logicData.conditions, logicData.action, container);

      /* console.log('Field evaluation:', {
        fieldId: field.getAttribute('data-field-id'),
        shouldShow: shouldShow,
        action: logicData.action,
        conditions: logicData.conditions,
        enabled: logicData.enabled,
        explanation: `Action is "${logicData.action}" - field will be ${shouldShow ? 'shown' : 'hidden'}`
      }); */

      if (shouldShow) {
        field.classList.remove('hidden');
        field.style.display = '';
      } else {
        field.classList.add('hidden');
        field.style.display = 'none';
      }
    } catch (error) {
      console.error('Error evaluating conditional logic:', error, field);
    }
  });
}

// Function to evaluate individual conditions
function evaluateConditions(conditions, action, container) {
  if (!conditions || conditions.length === 0) return true;

  let allConditionsMet = true;

  for (const condition of conditions) {
    // Try multiple selectors to find the target field
    let targetField = null;

    // First try to find the input/select/textarea directly
    targetField = container.querySelector(`input[data-field-id="${condition.field}"], select[data-field-id="${condition.field}"], textarea[data-field-id="${condition.field}"]`);

    // If not found, try to find the container div and then the input inside
    if (!targetField) {
      const containerDiv = container.querySelector(`[data-field-id="${condition.field}"]`);
      if (containerDiv) {
        targetField = containerDiv.querySelector('input, select, textarea');
      }
    }

    // Last resort - try by ID
    if (!targetField) {
      targetField = container.querySelector(`#${condition.field}`);
    }

    if (!targetField) {
      // console.log('Target field not found for condition:', condition.field);
      // console.log('Available fields with data-field-id:', Array.from(container.querySelectorAll('[data-field-id]')).map(f => f.getAttribute('data-field-id')));
      allConditionsMet = false;
      break;
    }

    const fieldValue = getFieldValue(targetField);
    const conditionMet = evaluateSingleCondition(fieldValue, condition.operator, condition.value);

    /* console.log('Condition check:', {
      field: condition.field,
      targetFieldFound: !!targetField,
      targetFieldType: targetField ? targetField.type : 'none',
      fieldValue: fieldValue,
      operator: condition.operator,
      conditionValue: condition.value,
      conditionMet: conditionMet
    }); */

    if (!conditionMet) {
      allConditionsMet = false;
      break;
    }
  }

  // Return true if we should show the field
  return action === 'show' ? allConditionsMet : !allConditionsMet;
}

// Function to get field value based on field type
function getFieldValue(field) {
  if (!field) return '';

  // If it's a container div, find the actual input inside it
  if (field.tagName === 'DIV') {
    const input = field.querySelector('input, select, textarea');
    if (input) {
      field = input;
    } else {
      return '';
    }
  }

  if (field.type === 'checkbox') {
    if (field.classList && field.classList.contains('switch-input')) {
      // Toggle checkbox
      return field.checked ? (field.value || 'on') : '';
    } else {
      // Regular checkbox - check if any checkboxes with same name are checked
      const checkboxes = document.querySelectorAll(`input[name="${field.name}"]:checked`);
      const values = Array.from(checkboxes).map(cb => cb.value);
      return values.join(',');
    }
  } else if (field.type === 'radio') {
    const radioGroup = document.querySelectorAll(`input[name="${field.name}"]:checked`);
    return radioGroup.length > 0 ? radioGroup[0].value : '';
  } else if (field.tagName === 'SELECT') {
    return field.value || '';
  } else if (field.tagName === 'TEXTAREA') {
    return field.value || '';
  } else if (field.tagName === 'INPUT') {
    return field.value || '';
  } else {
    return '';
  }
}

// Function to evaluate a single condition
function evaluateSingleCondition(fieldValue, operator, conditionValue) {
  switch (operator) {
    case 'equals':
      return fieldValue === conditionValue;
    case 'not_equals':
      return fieldValue !== conditionValue;
    case 'contains':
      return fieldValue.toLowerCase().includes(conditionValue.toLowerCase());
    case 'not_contains':
      return !fieldValue.toLowerCase().includes(conditionValue.toLowerCase());
    case 'is_empty':
      return fieldValue === '' || fieldValue === null || fieldValue === undefined;
    case 'is_not_empty':
      return fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;
    default:
      return true;
  }
}

// Function to add progress indicator functionality
function addProgressIndicatorFunctionality(container) {
  const progressIndicator = container.querySelector('#progress-indicator');
  if (!progressIndicator) return;

  // Initial update
  updateProgressIndicator(container, 1);
}

// Function to update progress indicator
function updateProgressIndicator(container, currentPage) {
  const progressIndicator = container.querySelector('#progress-indicator');
  if (!progressIndicator) return;

  const pages = container.querySelectorAll('.page');
  const totalPages = pages.length;

  // Update label
  const label = progressIndicator.querySelector('.progress-label');
  if (label) {
    const labelText = label.textContent.split(' ')[0]; // Get the first word (e.g., "Step")
    label.textContent = `${labelText} ${currentPage} of ${totalPages}`;
  }

  // Update dots
  const dots = progressIndicator.querySelectorAll('.progress-dot');
  dots.forEach((dot, index) => {
    const pageNum = index + 1;
    dot.className = 'progress-dot';
    if (pageNum < currentPage) {
      dot.classList.add('completed');
    } else if (pageNum === currentPage) {
      dot.classList.add('active');
    }
  });

  // Update numbers
  const numbers = progressIndicator.querySelectorAll('.progress-number');
  numbers.forEach((number, index) => {
    const pageNum = index + 1;
    number.className = 'progress-number';
    if (pageNum < currentPage) {
      number.classList.add('completed');
    } else if (pageNum === currentPage) {
      number.classList.add('active');
    }
  });

  // Update progress bar
  const progressBar = progressIndicator.querySelector('.progress-bar');
  if (progressBar) {
    const percentage = totalPages > 1 ? ((currentPage - 1) / (totalPages - 1)) * 100 : 100;
    progressBar.style.width = `${percentage}%`;
  }
}

/**
 * Build WhatsApp URL with form data and settings
 * 
 * @param {object} settings - WhatsApp settings from form
 * @param {object} formData - Form submission data
 * @param {string} formId - Form ID
 * @returns {string} - WhatsApp URL
 */
function buildWhatsAppUrl(settings, formData, formId) {
  let message = settings.whatsapp_message || 'New form submission';
  
  // Replace placeholders
  const replacements = {
    '{FormName}': settings.form_name || '',
    '{PageURL}': window.location.href,
    '{Date}': new Date().toLocaleDateString(),
    '{Time}': new Date().toLocaleTimeString(),
    '{FormID}': formId || ''
  };
  
  for (const [placeholder, value] of Object.entries(replacements)) {
    const regex = new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g');
    message = message.replace(regex, value);
  }
  
  // Use PHP-prepared form_fields (captcha stripped, esignatures as WP URLs) when available
  const fieldsToUse = (settings.form_fields && typeof settings.form_fields === 'object')
    ? settings.form_fields
    : formData;

  // Include form data if enabled
  if (settings.whatsapp_include_leads && fieldsToUse) {
    message += '\n\n--- Form Data ---\n';
    for (const [key, value] of Object.entries(fieldsToUse)) {
      // When falling back to raw formData, apply client-side filtering
      if (!settings.form_fields) {
        if (key === 'action' || key === 'nonce' || key === 'form_id') continue;
        if (
          key === 'cf-turnstile-response' ||
          key === 'g-recaptcha-response' ||
          key === 'h-captcha-response' ||
          key.toLowerCase().includes('custom-captcha-input') ||
          key.endsWith('_correct')
        ) continue;
        if (key.startsWith('esignature-')) {
          message += `${key}: [Signature — view in email]\n`;
          continue;
        }
      }
      message += `${key}: ${value}\n`;
    }
  }
  
  // Include metadata if enabled
  if (settings.whatsapp_include_metadata) {
    message += '\n\n--- Metadata ---\n';
    message += `Page: ${window.location.href}\n`;
    message += `Date: ${new Date().toLocaleDateString()}\n`;
    message += `Time: ${new Date().toLocaleTimeString()}\n`;
  }
  
  // Clean phone number (remove spaces, dashes, parentheses, etc.)
  const cleanNumber = settings.whatsapp_number.replace(/[^0-9+]/g, '');
  
  // Encode message for URL
  const encodedMessage = encodeURIComponent(message);
  
  // Detect mobile vs desktop and build appropriate URL
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // Mobile: Use wa.me format
    return `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
  } else {
    // Desktop: Use web.whatsapp.com format
    return `https://web.whatsapp.com/send?phone=${cleanNumber}&text=${encodedMessage}`;
  }
}

/**
 * Handle WhatsApp redirection after form submission
 * 
 * @param {object} settings - WhatsApp settings from form
 * @param {object} formData - Form submission data
 * @param {string} formId - Form ID
 */
function handleWhatsAppRedirection(settings, formData, formId) {
  // Check if WhatsApp redirection is enabled and number is provided
  if (!settings || !settings.whatsapp_redirect_enabled || !settings.whatsapp_number) {
    return;
  }
  
  try {
    const whatsappUrl = buildWhatsAppUrl(settings, formData, formId);
    
    // Small delay to ensure form submission completes
    setTimeout(() => {
      if (settings.whatsapp_open_new_tab) {
        window.open(whatsappUrl, '_blank');
      } else {
        window.location.href = whatsappUrl;
      }
    }, 500);
  } catch (error) {
    console.error('WhatsApp redirection error:', error);
  }
}

// Import authentication form scripts
import './auth/auth-forms-handler.js';
import './auth/form-validation.js';
import './auth/password-strength.js';
import './auth/username-checker.js';
